import {isEmpty} from 'lodash';

interface RgbCodeType {
  r: number;
  g: number;
  b: number;
}

type HexToRgbResult = RgbCodeType | null;

interface ColorUtilsType {
  hsv2rgb: (h: number, s: number, v: number) => string;
  hexToRgb: (hex: string) => HexToRgbResult;
  interpolate: (
    start: number,
    end: number,
    steps: number,
    count: number
  ) => number;
  colorValueToHex: (value: number) => string;
  rgbToHex: (color: RgbCodeType) => string;
  rgbToRgbaString: (color: RgbCodeType, alpha: number) => string;
  getHSVLinearGradientColor: (value, from, to) => string;
  getRGBLinearGradientColor: (
    value: number,
    negative: RgbCodeType,
    positive: RgbCodeType,
    neutral: RgbCodeType
  ) => string;
  getLuminanceFromRGB: (color: RgbCodeType) => number;
  getContrastTextColor: (hexColor: string) => string;
  getDarkerColor: (hexColor: string) => string;
}

const ColorUtils: ColorUtilsType = {
  hsv2rgb(h, s, v) {
    // adapted from http://schinckel.net/2012/01/10/hsv-to-rgb-in-javascript/
    let rgb: number[];
    let i: number;
    let data: number[] = [];
    if (s === 0) {
      rgb = [v, v, v];
    } else {
      const normH = h / 60;
      i = Math.floor(normH);
      data = [
        v * (1 - s),
        v * (1 - s * (normH - i)),
        v * (1 - s * (1 - (normH - i))),
      ];
      switch (i) {
        case 0:
          rgb = [v, data[2], data[0]];
          break;
        case 1:
          rgb = [data[1], v, data[0]];
          break;
        case 2:
          rgb = [data[0], v, data[2]];
          break;
        case 3:
          rgb = [data[0], data[1], v];
          break;
        case 4:
          rgb = [data[2], data[0], v];
          break;
        default:
          rgb = [v, data[0], data[1]];
          break;
      }
    }
    return `#${rgb.map((x) => this.colorValueToHex(x * 255)).join('')}`;
  },

  hexToRgb(hex) {
    let result: HexToRgbResult = null;
    if (!isEmpty(hex)) {
      // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      const fullHex = hex.replace(
        shorthandRegex,
        (m, r, g, b) => r + r + g + g + b + b
      );

      const fullResult = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
        fullHex
      );
      result = fullResult
        ? {
            r: parseInt(fullResult[1], 16),
            g: parseInt(fullResult[2], 16),
            b: parseInt(fullResult[3], 16),
          }
        : null;
    }
    return result;
  },

  interpolate(start, end, steps, count) {
    return Math.floor(start + ((end - start) / steps) * count);
  },

  colorValueToHex(value) {
    return `0${Math.round(value).toString(16)}`.slice(-2);
  },

  rgbToHex(color) {
    return `#${this.colorValueToHex(color.r)}${this.colorValueToHex(
      color.g
    )}${this.colorValueToHex(color.b)}`;
  },

  rgbToRgbaString(color, alpha = 1) {
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
  },

  getHSVLinearGradientColor(value, from, to) {
    const diff = to - from;
    const h = Math.floor((value * diff) / 100) + from;
    const s = Math.abs(value - 50) / 50;
    const v = 1;

    return this.hsv2rgb(h, s, v);
  },

  getRGBLinearGradientColor(
    value,
    negative = {r: 255, g: 6, b: 60},
    positive = {r: 6, g: 190, b: 60},
    neutral = {r: 255, g: 255, b: 255}
  ) {
    let currValue = value;
    let start = negative;
    let end = neutral;

    if (value > 50) {
      start = neutral;
      end = positive;
      currValue = value % 51;
    }

    return this.rgbToHex({
      r: this.interpolate(start.r, end.r, 50, currValue),
      g: this.interpolate(start.g, end.g, 50, currValue),
      b: this.interpolate(start.b, end.b, 50, currValue),
    });
  },

  getLuminanceFromRGB({r, g, b}) {
    const [rLum, gLum, bLum] = [r, g, b].map((color) => {
      let c = color;
      c /= 255.0;
      if (c <= 0.03928) {
        c /= 12.92;
      } else {
        c = ((c + 0.055) / 1.055) ** 2.4;
      }
      return c;
    });

    return 0.2126 * rLum + 0.7152 * gLum + 0.0722 * bLum;
  },

  getContrastTextColor(hexColor) {
    const rgbColor = this.hexToRgb(hexColor);
    let result = '#000000';
    if (rgbColor !== null) {
      const luminance = this.getLuminanceFromRGB(rgbColor);
      result =
        luminance > Math.sqrt(1.05 * 0.05) - 0.05 ? '#000000' : '#ffffff';
    }

    return result;
  },

  getDarkerColor(hexColor) {
    let result = '#000000';
    const rgbColor = this.hexToRgb(hexColor);
    if (rgbColor !== null) {
      const {r, g, b} = rgbColor;
      const r1 = r * 0.75;
      const g1 = g * 0.75;
      const b1 = b * 0.75;
      result = this.rgbToHex({r: r1, g: g1, b: b1});
    }

    return result;
  },
};

export default ColorUtils;
